import React, {
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import Tooltip from 'core/components/Tooltip';
import clsx from 'clsx';
import {
  isEmptyString,
} from '@populateui/utils';
import Popover from 'core/components/Popover';
import IconButton from '@mui/material/IconButton';
import AddendumIcon from 'core/icons/addendumIcon';
import CloseIcon from '@mui/icons-material/Close';
import {
  useForm,
  useFormContext,
  FormProvider,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import Button from 'pages/Dashboard/components/Button';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import isNil from 'lodash/isNil';
import NotesIcon from 'core/icons/notes';
import ServiceLineStatusBadge from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ServiceLineStatusBadge';

type Props = {
  data: ServiceAllocation;
  index?: number;
};

export type EOBServiceCodeCommentParams = {
  index: number;
  comment: string;
  data: ServiceAllocation;
};

type FormData = { comment: string };

type CommentFormProps = {
  serviceName?: string;
  comment?: string;
  onUpdate: (props: FormData) => void;
  onClose: () => void;
};

function CommentForm({
  serviceName,
  comment,
  onUpdate,
  onClose,
}: CommentFormProps) {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useFormContext<FormData>();

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textareaElement = inputRef?.current;
    const commentLength = comment?.length ?? 0;
    if (!isNil(textareaElement)) {
      textareaElement.focus();
      textareaElement.setSelectionRange(commentLength, commentLength);
    }
  }, []);

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="font-semibold text-sm">
          Comment for
          {' '}
          {serviceName}
        </div>
        <div>
          <IconButton
            size="small"
            disableRipple
            disabled={!isValid}
            onClick={onClose}
          >
            <CloseIcon className="!w-5 !h-5 text-gray-700" />
          </IconButton>
        </div>
      </div>
      <Controller
        control={control}
        name="comment"
        render={({ field }) => (
          <TextAreaWithActions
            {...field}
            placeholder="Add Comment"
            maxRows={2}
            rows={1}
            id="service-code-comment"
            trackingLabel="Service Code Comment"
            maxLength={255}
            className="!max-h-[120px] pr-1"
            inputRef={inputRef as RefObject<HTMLTextAreaElement>}
          />
        )}
      />
      <div className="flex justify-between items-center gap-2">
        <Button
          variant="outlined"
          type="button"
          className="flex-1 !max-w-[unset]"
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          variant="contained"
          className="flex-1 !max-w-[unset]"
          color="primary"
          type="submit"
          disabled={!isValid}
          onClick={handleSubmit(onUpdate)}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
}

export default function EOBServiceCodeCell({
  data,
  index,
}: Props) {
  const { comment, serviceName } = data;
  const isCommentEmpty = isEmptyString(comment);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const formProps = useForm<FormData>({
    defaultValues: { comment: comment ?? '' },
  });

  const { reset } = formProps;

  const handleClose = () => {
    setAnchorEl(null);
    reset({ comment: comment ?? '' });
  };

  const onSubmit = ({ comment }: FormData) => {
    paymentsEventChannel.emit(
      'onChangeEOBServiceCodeComment',
      { index: index ?? 0, comment, data },
    );
    setAnchorEl(null);
  };

  return (
    <div className="flex items-center gap-1">
      <Tooltip
        title={isCommentEmpty
          ? 'Add comment'
          : (
            <div className="flex flex-col gap-2 max-w-[200px]">
              {comment}
            </div>
          )}
        placement="top-start"
        isInteractive
      >
        <IconButton
          size="small"
          disableRipple
          tabIndex={-1}
          color="info"
          className="!p-0"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
          }}
        >
          <AddendumIcon className={clsx('!w-4 !h-4', {
            '!text-gray-500': isCommentEmpty,
            '!text-blue-500': !isCommentEmpty,
          })}
          />
        </IconButton>
      </Tooltip>
      <div className="flex items-center gap-0.5">
        <span className="truncate">
          {serviceName}
        </span>
        <ServiceLineStatusBadge data={data} />
      </div>
      {!isNil(anchorEl) && (
        <Popover
          anchorEl={anchorEl}
          onClose={handleClose}
          disableEnforceFocus
          disableScrollLock
          disableAutoFocus
          disableRestoreFocus
          classes={{ paper: '!w-[520px] ' }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <FormProvider {...formProps}>
            <CommentForm
              serviceName={serviceName}
              comment={comment ?? ''}
              onUpdate={onSubmit}
              onClose={handleClose}
            />
          </FormProvider>
        </Popover>
      )}
    </div>
  );
}

export function ReadOnlyServiceCode({ data }: Props) {
  const { comment, serviceName } = data;
  const isCommentEmpty = isEmptyString(comment);

  return (
    <Tooltip
      title={(
        <div className="max-w-[180px]">
          {comment}
        </div>
      )}
      hide={isCommentEmpty}
      placement="top-start"
      isInteractive
    >
      <div className="flex items-center gap-1">
        {!isCommentEmpty && (
          <NotesIcon className={clsx('!w-4 !h-4 !text-blue-500')} />
        )}
        <span className="truncate">{serviceName}</span>
        <ServiceLineStatusBadge data={data} />
      </div>
    </Tooltip>
  );
}
